var _excluded = ["bottom", "className", "children", "enabled", "onContainerSize", "onStick", "onUnstick", "portalContainer", "portalContainerClass", "stickToBody", "stickToBottom", "top", "utils"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Global } from '@emotion/react';
import { createHtmlPortalNode, InPortal, OutPortal } from 'react-reverse-portal';
import StyledSticky from './sticky.styles';
import StyledStickyAnchor from './styles/sticky.anchor.styles';
import StickyPortalStyles from './styles/sticky.portal.styles';
import generateID from './../../utilities/generateID';
import ContainerQuery from './../container-query';
import Portal from '../portal';
import StyledLayoutHeader from '../layout/components/layout-header/layout.header.styles';
import { breakpoints, StyledCacheWrapper } from './../../styles';
import useLayout from '../layout/layout.hook';
var StickyPropTypes = {
  /** Add gap between sticky component and bottom of scroll (for use with `stickToBottom`). */
  bottom: function bottom(props, propName, componentName) {
    if (props[propName] !== 'auto' && isNaN(props[propName])) {
      return new Error('Invalid prop `' + propName + '` supplied to' + ' `' + componentName + '`.');
    }
  },
  /** Add class name */
  className: PropTypes.string,
  /** Add children */
  children: PropTypes.node,
  /** Whether the sticky functionality is enabled */
  enabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool
  })]),
  /** Callback for when component resizes */
  onContainerSize: PropTypes.func,
  /** Callback for when component becomes stuck. */
  onStick: PropTypes.func,
  /** Callback for when component becomes unstuck. */
  onUnstick: PropTypes.func,
  /** Portal container */
  portalContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Portal container class name */
  portalContainerClass: PropTypes.string,
  /** Element will always stick to the body instead of a scrollable parent. */
  stickToBody: PropTypes.bool,
  /** Stick element to bottom of parent instead of top. */
  stickToBottom: PropTypes.bool,
  /** Add gap between sticky component and top of scroll. 'Auto' will stick under the Masthead. */
  top: function top(props, propName, componentName) {
    if (props[propName] !== 'auto' && isNaN(props[propName])) {
      return new Error('Invalid prop `' + propName + '` supplied to' + ' `' + componentName + '`.');
    }
  },
  /** Add utility objects for styling */
  utils: PropTypes.object
};
var defaultProps = {
  bottom: 'auto',
  className: '',
  children: /*#__PURE__*/React.createElement(React.Fragment, null),
  enabled: true,
  onContainerSize: function onContainerSize() {},
  onStick: function onStick() {},
  onUnstick: function onUnstick() {},
  portalContainer: null,
  portalContainerClass: '',
  stickToBody: true,
  stickToBottom: false,
  top: 'auto',
  utils: {}
};

/**
 * @ignore @description
 * ## Sticky
 * The Sticky component wraps a child and listens for the scroll position of its nearest scrollable parent to reach it. Once the sticky component reaches the top of the scrollable element, it will become stuck to the top.
 *
 * @ignore @see Mesh: [Sticky Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/scaffolding-sticky--default)
 *
 * @ignore @example
 * ```jsx
 * import { Sticky, Box, Type } from 'mesh-component-library';
 *
 * <div style={{ overflowY: 'scroll', height: 200 }} tabIndex="0">
 *  <div style={{ padding: 30 }}>
 *    <Sticky>
 *      <Box>
 *        <Type weight="bold">I will stick to the top of the scrollable when reached.</Type>
 *      </Box>
 *    </Sticky>
 *    <br />
 *    <p>Lorem ipsum dolor sit amet...</p>
 *    <div style={{ height: 400 }} />
 *  </div>
 * </div>
 * ```
 */

var Sticky = function Sticky(props) {
  var bottom = props.bottom,
    className = props.className,
    children = props.children,
    enabled = props.enabled,
    _onContainerSize = props.onContainerSize,
    onStick = props.onStick,
    onUnstick = props.onUnstick,
    portalContainerProp = props.portalContainer,
    portalContainerClassProp = props.portalContainerClass,
    stickToBody = props.stickToBody,
    stickToBottom = props.stickToBottom,
    top = props.top,
    utils = props.utils,
    other = _objectWithoutProperties(props, _excluded);

  // Create a portal node to hold your rendered content
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    shouldCreatePortal = _React$useState2[0],
    setShouldCreatePortal = _React$useState2[1];
  var portalNode = useMemo(function () {
    return shouldCreatePortal ? createHtmlPortalNode() : null;
  }, [shouldCreatePortal]);
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isAttachedToBody = _React$useState4[0],
    setIsAttachedToBody = _React$useState4[1]; // Portal is attached to body when stuck
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    isPortalActive = _React$useState6[0],
    setIsPortalActive = _React$useState6[1]; // Portal is active when element is stuck
  var _React$useState7 = React.useState(null),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    portalContainer = _React$useState8[0],
    setPortalContainer = _React$useState8[1];
  var _React$useState9 = React.useState({
      height: 0,
      width: 0
    }),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    stickyElementSize = _React$useState10[0],
    setStickyElementSize = _React$useState10[1]; // Size of sticky element before it becomes stuck
  var _React$useState11 = React.useState(0),
    _React$useState12 = _slicedToArray(_React$useState11, 2),
    stickyPositionTop = _React$useState12[0],
    setStickyPositionTop = _React$useState12[1]; // Position from top element should stick (if !stickToBottom)

  var anchorNodeRef = useRef(null); // anchor remains in original place when element sticks
  var eventTargetRef = useRef(null); // scrollable element to which event listeners are attached
  var isEnabledRef = useRef(null);
  var isLayoutHeaderSticky = useRef(null);
  var prevPortalActiveRef = useRef(null);
  var _useRef = useRef(portalContainerClassProp || generateID('sticky_')),
    portalContainerClass = _useRef.current; // generate random class name for Portal

  var _useLayout = useLayout(),
    _useLayout$layoutStat = _useLayout.layoutState,
    layoutState = _useLayout$layoutStat === void 0 ? {} : _useLayout$layoutStat; // get the state of the containing Layout (if applicable)

  var _getNearestScrollableParent = function getNearestScrollableParent(node) {
    // get scrollable style
    var _window$getComputedSt = window.getComputedStyle(node),
      overflowY = _window$getComputedSt.overflowY;
    // if 'auto' or 'scroll', element is scrollable
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    if (node.parentNode instanceof HTMLElement) {
      // recursively search parent for scrollable element
      return _getNearestScrollableParent(node.parentNode);
    }
    return window;
  };
  var handleEnabledProp = function handleEnabledProp() {
    if (typeof enabled === 'boolean') {
      isEnabledRef.current = enabled;
      return;
    }
    // handle responsive enabled prop
    for (var _i = 0, _Object$entries = Object.entries(breakpoints); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
        key = _Object$entries$_i[0],
        value = _Object$entries$_i[1];
      if (window.innerWidth >= value[0] && enabled.hasOwnProperty(key)) {
        isEnabledRef.current = enabled[key];
      }
    }
  };
  var handleResizeEvent = function handleResizeEvent() {
    // update enabled ref (required for handling responsive value)
    handleEnabledProp();
    // trigger initial scroll event
    handleScrollEvent({
      target: eventTargetRef.current
    });
  };
  var getNearestLayoutHeader = function getNearestLayoutHeader() {
    // check if the Layout.Header component is rendered
    var nearestLayoutHeader = document.querySelector(StyledLayoutHeader);
    if (!nearestLayoutHeader || !isLayoutHeaderSticky.current || nearestLayoutHeader.contains(anchorNodeRef.current)) {
      return null;
    }
    // component should stick below Layout.Header
    return nearestLayoutHeader;
  };
  var getScrollerPosition = function getScrollerPosition(target) {
    return target instanceof HTMLElement ? target.getBoundingClientRect() : {};
  };
  var handleScrollEvent = function handleScrollEvent(_ref) {
    var target = _ref.target;
    // if stickiness is not enabled, Portal should be inactive
    if (isEnabledRef.current !== true) {
      setIsPortalActive(false);
      return;
    }
    // get the position of the anchor from the top and bottom
    var _anchorNodeRef$curren = anchorNodeRef.current.getBoundingClientRect(),
      anchorPositionBottom = _anchorNodeRef$curren.bottom,
      anchorPositionTop = _anchorNodeRef$curren.top;
    var isTargetHTMLElement = target instanceof HTMLElement;
    // get the position of the scroller from the top and its height (if applicable)
    var _getScrollerPosition = getScrollerPosition(target),
      scrollerHeight = _getScrollerPosition.height,
      scrollerPositionTop = _getScrollerPosition.top;
    if (stickToBottom) {
      // stuck state will depend on height of the scroller
      var targetHeight = isTargetHTMLElement ? scrollerHeight : window.innerHeight;
      if (isTargetHTMLElement) {
        // set `anchorPositionBottom` relative to scroller instead of body
        anchorPositionBottom -= scrollerPositionTop;
      }
      if (bottom !== 'auto') {
        anchorPositionBottom += bottom;
      }
      // if anchor hasn't reached the point at which it should unstick, set the Portal to active
      setIsPortalActive(anchorPositionBottom > targetHeight);
    } else {
      var stickAtPositionTop = 0;
      // check if the Layout.Header component is rendered
      var nearestLayoutHeader = getNearestLayoutHeader();
      if (nearestLayoutHeader) {
        // component should stick below Layout.Header
        var _nearestLayoutHeader$ = nearestLayoutHeader.getBoundingClientRect(),
          layoutHeaderHeight = _nearestLayoutHeader$.height;
        anchorPositionTop -= layoutHeaderHeight;
        stickAtPositionTop += layoutHeaderHeight;
      }
      if (isTargetHTMLElement) {
        // set `anchorPositionTop` relative to scroller instead of body
        anchorPositionTop -= scrollerPositionTop;
      }
      if (top !== 'auto') {
        anchorPositionTop -= top;
        stickAtPositionTop += top;
      }
      // if anchor is at or past the point at which it should become stuck, set the Portal to active
      setIsPortalActive(anchorPositionTop <= 0);
      setStickyPositionTop(stickAtPositionTop);
    }
  };
  var onRefSet = useCallback(function (node) {
    if (!node) {
      return;
    }
    anchorNodeRef.current = node;
    // set scrollable ref to window or nearest scrollable element
    eventTargetRef.current = stickToBody ? window : _getNearestScrollableParent(node);
    // add scroll event listener to scrollable element
    eventTargetRef.current.addEventListener('scroll', handleScrollEvent);
  }, []);
  useEffect(function () {
    if (!isPortalActive && !portalContainer) {
      // the Portal container will be placed in either the scroller or the element referenced in the portalContainerProp
      var nextPortalContainer = document.querySelector(portalContainerProp) || eventTargetRef.current;
      // check if the portal will be attached to the body
      var willAttachToBody = !(nextPortalContainer instanceof HTMLElement);
      if (!willAttachToBody) {
        // portal is being attached to another HTML element
        if (!stickToBottom) {
          // portal container will need to be at the beginning of parent
          var prependedPortalContainer = document.createElement('div');
          // add the generated portal container class
          prependedPortalContainer.className = portalContainerClass;
          // insert portal container before first child of scroller
          nextPortalContainer.insertBefore(prependedPortalContainer, nextPortalContainer.firstChild);
          // update `nextPortalContainer` to be new prepended container
          nextPortalContainer = prependedPortalContainer;
        }
        // set portal container to new prepended portal
        setPortalContainer(nextPortalContainer);
      }
      setIsAttachedToBody(willAttachToBody);
    }
  }, [isPortalActive, portalContainerProp, stickToBottom]);
  useEffect(function () {
    if (!isPortalActive === prevPortalActiveRef.current) {
      // trigger stick / unstick callback
      if (isPortalActive) {
        onStick();
      } else {
        onUnstick();
      }
    }
    prevPortalActiveRef.current = isPortalActive;
  }, [isPortalActive]);
  useEffect(function () {
    // stick node must be measured before sticking to ensure correct anchor height
    if (stickyElementSize.hasOwnProperty('height')) {
      handleResizeEvent();
    }
  }, [stickyElementSize]);
  useEffect(function () {
    isLayoutHeaderSticky.current = layoutState.isHeaderSticky;
  }, [layoutState]);
  useEffect(function () {
    // safe to create HTML portal
    setShouldCreatePortal(true);
    // add listener to resize event, (required for handling responsive `enabled` prop value)
    window.addEventListener('resize', handleResizeEvent);
    // remove resize and scroll event listeners on unmount
    return function () {
      window.removeEventListener('resize', handleResizeEvent);
      eventTargetRef.current.removeEventListener('scroll', handleScrollEvent);
    };
  }, []);
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(Global, {
    styles: StickyPortalStyles({
      bottom: bottom,
      isAttachedToBody: isAttachedToBody,
      portalContainerClass: portalContainerClass,
      stickToBottom: stickToBottom,
      stickyPositionTop: stickyPositionTop
    })
  }), !stickToBottom && /*#__PURE__*/React.createElement(StyledStickyAnchor, {
    ref: onRefSet,
    isPortalActive: isPortalActive,
    stickyElementSize: stickyElementSize
  }), portalNode && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InPortal, {
    node: portalNode
  }, /*#__PURE__*/React.createElement(ContainerQuery, {
    onContainerSize: function onContainerSize(size) {
      if (size.height !== undefined) {
        setStickyElementSize(size);
      }
      _onContainerSize(size);
    }
  }, /*#__PURE__*/React.createElement(StyledSticky, _extends({
    className: className,
    utils: utils
  }, other), children))), /*#__PURE__*/React.createElement(Portal, {
    container: portalContainer,
    containerClass: portalContainerClass,
    hasPortal: isPortalActive
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(OutPortal, {
    node: portalNode
  })))), stickToBottom && /*#__PURE__*/React.createElement(StyledStickyAnchor, {
    ref: onRefSet,
    isPortalActive: isPortalActive,
    stickyElementSize: stickyElementSize
  }));
};
Sticky.propTypes = StickyPropTypes;
Sticky.defaultProps = defaultProps;
export default Sticky;